import React from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Page } from "../components/layout"
import { Card, type CardTrimColor } from "../components/card/Card"
import "./accelerator.scss"
import { UniversalLink } from "../components/UniversalLink"

const programBenefits = [
  {
    title: `Program funding`,
    description: `The XRPL Accelerator Program offers up to $200K in funding which includes development and adoption-based milestones.`,
  },
  {
    title: `Mentorship`,
    description: (
      <>
        Unlocks access to mentorship opportunities spanning the XRPL ecosystem
        and leveraging our partner{` `}
        <UniversalLink
          to="https://tenity.com/"
          className={`text-[#7919FF] font-['Work_Sans'] text-lg font-medium leading-[133%] transition-colors hover:text-purple-800`}
        >
          Tenity
        </UniversalLink>
        ’s and{` `}
        <UniversalLink
          to="https://www.difc.ae/"
          className={`text-[#7919FF] font-['Work_Sans'] text-lg font-medium leading-[133%] transition-colors hover:text-purple-800`}
        >
          DIFC
        </UniversalLink>
        ’s extensive network.
      </>
    ),
  },
  {
    title: `Tailored program curriculum`,
    description: `A tailored curriculum, designed to address the specific needs of each startup and provide comprehensive coverage of essential XRPL ecosystem topics.`,
  },
  {
    title: `Network Expansion`,
    description: `Join a dynamic community of innovators and entrepreneurs pioneering cutting-edge products at the forefront of technology on the XRP Ledger. We provide valuable exposure and networking opportunities, connecting startups with key players and fostering relationships within the web3 funding ecosystem.`,
  },
]

const whoShouldApply = [
  {
    trim: `pink_red`,
    description: `Ambitious founding teams eager to explore the potential of the XRP Ledger in shaping innovative products.`,
  },
  {
    trim: `yellow`,
    description: `You must have an MVP - the more traction you've garnered, the stronger your application.`,
  },
  {
    trim: `blue_green`,
    description: `Your product should address a distinct pain point and possess significant market potential.`,
  },
]

const cohorts2025 = [
  {
    id: 5,
    name: `Cohort 5`,
    dates: `March 10 – May 16, 2025`,
    location: `Hybrid`,
    format: `Remote & on-site demo week`,
    partner: `DIFC`,
    partnerLogo: `/images/affiliations/DIFC.svg`,
    partnerUrl: `https://www.difc.ae/`,
    timelineColor: `magenta`,
    applicationOpens: null,
    applicationCloses: null,
  },
  {
    id: 6,
    name: `Cohort 6`,
    dates: `June 9 – Aug 15, 2025`,
    location: `Hybrid (on-site elements will take place in Singapore)`,
    format: `Remote & on-site demo week`,
    partner: `Tenity`,
    partnerLogo: `/images/affiliations/tenity.svg`,
    partnerUrl: `https://tenity.com/`,
    timelineColor: `pure_purple`,
    applicationOpens: null,
    applicationCloses: null,
  },
  {
    id: 7,
    name: `Cohort 7`,
    dates: `Sep – Nov, 2025`,
    location: `Hybrid (on-site elements will take place in Singapore)`,
    format: `Remote & on-site demo week`,
    partner: `Tenity`,
    partnerLogo: `/images/affiliations/tenity.svg`,
    partnerUrl: `https://tenity.com/`,
    timelineColor: `green`,
    applicationOpens: `Open`,
    applicationCloses: `Aug 17, 2025`,
  },
]

export const Accelerator = () => {
  const [expandedCohorts, setExpandedCohorts] = React.useState<Set<number>>(
    new Set([5])
  )

  const toggleCohort = (cohortId: number) => {
    const newExpanded = new Set(expandedCohorts)
    if (newExpanded.has(cohortId)) {
      newExpanded.delete(cohortId)
    } else {
      newExpanded.add(cohortId)
    }
    setExpandedCohorts(newExpanded)
  }

  return (
    <Page
      header="Accelerate Your Vision on the XRP Ledger"
      title="XRPL Accelerator"
      keywords="crypto grants, ripple funding, blockchain grants, web3 grants, xrpl grants, ripple fund, developer funding, developer grants, open source funding, ripple grants, blockchain funding, web3 funding, software developer grants, xrp ledger funding, xrpl funding, xrpl awards, open source developer grants"
      description="Explore opportunities for startups on the XRP Ledger."
      slug="home"
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 md:text-center md:grid-cols-8 lg:grid-cols-12">
        <h3 className="col-span-2 apply-sub-heading sm:col-span-4 md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3">
          Explore opportunities for startups on the XRP Ledger.
          <br />
        </h3>
      </div>
      <div className="subscribe-for-updates-btn">
        <UniversalLink
          className="btn btn-primary btn-auto"
          to="https://submit.xrplgrants.org/submit"
        >
          Apply Now
        </UniversalLink>
      </div>
      <div className="mt-20 w-full dev-resources">
        <div className="flex flex-col gap-6">
          <h2 className="italic">What is the Accelerator?</h2>
          <div className="flex flex-col gap-6">
            <p>
              XRPL Accelerator is a 12-week program to support entrepreneurs
              building on the XRP Ledger. All programs are taking a hybrid
              approach with the majority of the curriculum being available
              virtually; with the opportunity to attend an onsite demo day.
            </p>
            <p>
              Each program will admit between 5-12 teams and focus on projects
              that will drive institutional DeFi adoption and meaningful
              economic activity on the XRP Ledger this includes but is not
              limited to:
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>
                Real World Assets (Primary Issuance / Token Issuers, Asset
                Managers, Token Issuance Platforms)
              </li>
              <li>
                DeFi (High-Quality Tokens, DEX & AMM, Lending & Borrowing,
                Institutional DeFi & Market Infrastructure, Stablecoin Adoption
                & Utility, Payment Infrastructure and Remittances).
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-20 w-full dev-resources accelerator">
        <div className="flex flex-col gap-12">
          <h2>Program benefits include</h2>
          <div className="grid grid-cols-1 gap-6 dev-res-cards sm:grid-cols-2 lg:grid-cols-3">
            {programBenefits.map((card) => (
              <Card
                key={card.title}
                className="min-w-80"
                trim={`blue_green` as CardTrimColor}
                title={card.title}
              >
                <p className="description">{card.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-20 w-full dev-resources who-apply">
        <div className="flex flex-col gap-12">
          <h2>Who should apply?</h2>
          <div className="grid grid-cols-1 gap-6 dev-res-cards sm:grid-cols-2 lg:grid-cols-3">
            {whoShouldApply.map((card) => (
              <Card
                key={card.description}
                className="min-w-80"
                trim={card.trim as CardTrimColor}
              >
                <p className="description">{card.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* 2025 Cohorts Section */}
      <div className="mt-20 w-full cohorts-section">
        <div className="flex flex-col gap-12">
          <h2 className="text-4xl font-bold text-black">2025 Cohorts</h2>
          <div className="flex flex-col gap-6">
            {cohorts2025.map((cohort) => (
              <div
                key={cohort.id}
                className="overflow-hidden bg-white rounded-lg border border-gray-200 cohort-card"
              >
                {/* Cohort Header */}
                <div
                  className="p-6 cursor-pointer cohort-header"
                  onClick={() => toggleCohort(cohort.id)}
                  onKeyDown={(e) => {
                    if (e.key === `Enter` || e.key === ` `) {
                      e.preventDefault()
                      toggleCohort(cohort.id)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={expandedCohorts.has(cohort.id)}
                >
                  <div className="mb-4">
                    <div className="flex justify-between items-baseline">
                      <h3 className="text-[#111112] text-2xl font-bold leading-[120%]">
                        {cohort.name}
                      </h3>
                      <button className="flex flex-shrink-0 gap-2 items-center font-medium text-purple-600 transition-colors hover:text-purple-800">
                        {expandedCohorts.has(cohort.id) ? `Less` : `More`}
                        <span
                          className={`transition-transform duration-200 ease-in-out ${
                            expandedCohorts.has(cohort.id)
                              ? `rotate-180`
                              : `rotate-0`
                          }`}
                        >
                          ↓
                        </span>
                      </button>
                    </div>
                    <p className="text-[#111112] font-['Work_Sans'] text-base font-medium leading-[150%]">
                      {cohort.dates}
                    </p>
                  </div>

                  {/* Timeline Bar */}
                  <div
                    className={`timeline-bar h-4 max-w-[90%] my-3 rounded-full bg-gradient-to-r trim-${cohort.timelineColor}`}
                  />
                </div>

                {/* Expanded Content */}
                <AnimatePresence initial={false}>
                  {expandedCohorts.has(cohort.id) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: `auto`,
                        opacity: 1,
                        transition: {
                          duration: 0.25,
                          ease: [0.25, 0.46, 0.45, 0.94],
                          opacity: { duration: 0.2 },
                          height: { duration: 0.25 },
                        },
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                        transition: {
                          duration: 0.18,
                          ease: [0.4, 0.0, 0.6, 1],
                          opacity: { duration: 0.1, ease: `linear` },
                          height: { duration: 0.18, ease: [0.4, 0.0, 0.6, 1] },
                        },
                      }}
                      style={{
                        overflow: `hidden`,
                        willChange: `height, opacity`,
                      }}
                      className="bg-white cohort-details"
                    >
                      <div className="p-6 pt-0">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-4">
                            <div className="flex flex-row gap-2">
                              <p className="mb-1 text-base font-semibold leading-[150%] text-[#111112]">
                                Program location:
                              </p>
                              <p className="text-[#111112] text-base font-normal leading-[150%]">
                                {cohort.location}
                              </p>
                            </div>
                            <div className="flex flex-row gap-2">
                              <p className="mb-1 text-base font-semibold leading-[150%] text-[#111112]">
                                Format:
                              </p>
                              <p className="text-[#111112] text-base font-normal leading-[150%]">
                                {cohort.format}
                              </p>
                            </div>
                            {cohort.applicationOpens && (
                              <div className="flex flex-row gap-2">
                                <p className="mb-1 text-base font-semibold leading-[150%] text-[#111112]">
                                  Application opens:
                                </p>
                                <p className="text-[#111112] text-base font-normal leading-[150%]">
                                  {cohort.applicationOpens}
                                </p>
                              </div>
                            )}
                            {cohort.applicationCloses && (
                              <div className="flex flex-row gap-2">
                                <p className="mb-1 text-base font-semibold leading-[150%] text-[#111112]">
                                  Application closes:
                                </p>
                                <p className="text-[#111112] text-base font-normal leading-[150%]">
                                  {cohort.applicationCloses}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col justify-start">
                            <p className="mb-2 text-base font-semibold leading-[150%] text-[#111112]">
                              Program partner:
                            </p>
                            <UniversalLink
                              to={cohort.partnerUrl}
                              className="inline-block"
                            >
                              <img
                                src={cohort.partnerLogo}
                                alt={cohort.partner}
                                className="object-contain mb-2 h-9 transition-opacity hover:opacity-80"
                              />
                            </UniversalLink>
                            <UniversalLink
                              to={cohort.partnerUrl}
                              className="mt-2 text-[#7919FF] font-['Work_Sans'] text-sm font-semibold leading-[143%] transition-colors hover:text-purple-800"
                            >
                              Learn About {cohort.partner}
                            </UniversalLink>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  )
}

export default Accelerator
